import React, { useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Stack, Button, Divider, Paper, Chip,
} from '@mui/material';
import * as XLSX from 'xlsx';
import PopoverFiltroRelatorio from './PopoverFiltroRelatorio';
import generatePdfRejeitadosCpfCnpj from './EditPdfRejeitadosCpfCnpj';
import { cores } from '../styles/styleUtils';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ── Proteções LGPD ────────────────────────────────────────────────────
const mascararCpf = (v) => {
  if (!v) return '—';
  const s = v.replace(/\D/g, '');
  if (s.length === 11) return `***.${s.slice(3,6)}.${s.slice(6,9)}-**`;
  return '***';
};
const mascararCnpj = (v) => {
  if (!v) return '—';
  const s = v.replace(/\D/g, '');
  if (s.length === 14) return `**.${s.slice(2,5)}.${s.slice(5,8)}/***/***-**`;
  return '***';
};
const mascararTelefone = (v) => {
  if (!v) return '—';
  const s = v.replace(/\D/g, '');
  if (s.length >= 10) return `(${s.slice(0,2)}) ${s.slice(2,7)}-**${s.slice(9)}`;
  return '***';
};

export default function RelatorioRejeitadasCpfCnpj({ data, filtro, setFiltro, onLimpar, type, isSupervisor }) {
  const [openFiltro, setOpenFiltro] = useState(null);

  const onCloseFiltro   = () => setOpenFiltro(null);
  const limparFiltro    = () => { setOpenFiltro(false); onLimpar(); };
  const onConfirmFiltro = (f) => { setOpenFiltro(false); setFiltro(f); };

  if (!data) return null;

  const linhasFiltradas = data.linhas.filter((p) => {
    const motivo = (p.motivoNome || '').toLowerCase();
    if (!motivo.includes('cpf reprovado') && !motivo.includes('cnpj reprovado')) return false;
    if (filtro?.consultor && p.user?._id !== filtro.consultor._id) return false;
    if (filtro?.produto   && p.produto?.nome !== filtro.produto)   return false;
    return true;
  });

  // Helpers para exibir dado real (supervisor) ou mascarado (consultor)
  const exibirCpfCnpj = (p) => {
    const isCpf = (p.motivoNome||'').toLowerCase().includes('cpf');
    const valor = isCpf ? p.cliente?.cpf : p.cliente?.cnpj;
    if (isSupervisor) return valor || '—';
    return isCpf ? mascararCpf(valor) : mascararCnpj(valor);
  };
  const exibirData = (p) => {
    const isCpf  = (p.motivoNome||'').toLowerCase().includes('cpf');
    const valor  = isCpf ? p.cliente?.nascimento : p.cliente?.datafundacao;
    if (!valor) return '—';
    if (isSupervisor) return new Date(valor).toLocaleDateString('pt-BR');
    return '**/**/****';
  };
  const exibirTelefone = (p) => {
    const valor = p.cliente?.telefone;
    if (isSupervisor) return valor || '—';
    return mascararTelefone(valor);
  };

  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheet = [];
    sheet.push(['Relatório de Propostas Reprovadas por CPF/CNPJ']);
    if (!isSupervisor) sheet.push(['⚠ Dados sensíveis mascarados por LGPD — acesso restrito ao supervisor.']);
    sheet.push([]);
    sheet.push(['Nome do Cliente','CPF/CNPJ','Data Nasc./Fundação','Telefone','Valor da Proposta','Consultor','Motivo']);
    linhasFiltradas.forEach((p) => {
      sheet.push([
        p.cliente?.nome,
        exibirCpfCnpj(p),
        exibirData(p),
        exibirTelefone(p),
        isSupervisor ? p.valor : '***',
        p.user?.nome,
        p.motivoNome,
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(sheet);
    XLSX.utils.book_append_sheet(wb, ws, 'CPF-CNPJ Reprovados');
    XLSX.writeFile(wb, 'relatorio-cpf-cnpj-reprovados.xlsx');
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}
        sx={{ '@media print': { display: 'none' } }}>
        <Button variant="outlined" onClick={() => setOpenFiltro(true)}>Filtro</Button>
        <Button variant="outlined" color="secondary" onClick={() => generatePdfRejeitadosCpfCnpj(data, filtro, isSupervisor)}>Exportar em PDF</Button>
        <Button variant="contained" onClick={exportarExcel}>Download Excel</Button>
      </Stack>

      {!isSupervisor && (
        <Chip label="🔒 Dados sensíveis protegidos por LGPD — CPF/CNPJ, telefone e datas são exibidos de forma parcial para este perfil"
          color="warning" sx={{ mb: 2, width: '100%', borderRadius: 1 }} />
      )}

      <Paper elevation={2} sx={{ p: 4, maxWidth: 900, mx: 'auto',
        '@media print': { boxShadow: 'none', p: 2 } }}>

        {/* Cabeçalho */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="h6" fontWeight="bold">Relatório de Propostas Reprovadas por CPF/CNPJ</Typography>
            <Typography variant="caption" color="text.secondary">
              {filtro?.datainicial && `De ${new Date(filtro.datainicial).toLocaleDateString('pt-BR')}`}
              {filtro?.datafinal  && ` até ${new Date(filtro.datafinal).toLocaleDateString('pt-BR')}`}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ mb: 3 }} />

        {/* KPIs */}
        <Stack direction="row" gap={2} mb={3} flexWrap="wrap">
          {[
            { label: 'CPF Reprovados',  valor: linhasFiltradas.filter(p => (p.motivoNome||'').toLowerCase().includes('cpf')).length },
            { label: 'CNPJ Reprovados', valor: linhasFiltradas.filter(p => (p.motivoNome||'').toLowerCase().includes('cnpj')).length },
            { label: 'Total',           valor: linhasFiltradas.length },
            { label: 'Valor Total',     valor: isSupervisor
                ? fmtBRL(linhasFiltradas.reduce((s, p) => s + (p.valor || 0), 0))
                : '🔒 Restrito' },
          ].map(({ label, valor }) => (
            <Box key={label} sx={{ flex: 1, minWidth: 120, textAlign: 'center',
              border: `1px solid ${cores.laranja}`, borderRadius: 1, p: 1, backgroundColor: '#FFF3E0' }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="subtitle1" fontWeight="bold" color={cores.laranja}>{valor}</Typography>
            </Box>
          ))}
        </Stack>

        {/* Tabela */}
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
          Propostas Reprovadas por CPF/CNPJ
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
              <TableCell><strong>Nome do Cliente</strong></TableCell>
              <TableCell><strong>CPF/CNPJ {!isSupervisor && '🔒'}</strong></TableCell>
              <TableCell><strong>Data Nasc./Fundação {!isSupervisor && '🔒'}</strong></TableCell>
              <TableCell><strong>Telefone {!isSupervisor && '🔒'}</strong></TableCell>
              {isSupervisor && <TableCell align="right"><strong>Valor da Proposta</strong></TableCell>}
              <TableCell><strong>Consultor</strong></TableCell>
              <TableCell><strong>Motivo</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linhasFiltradas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isSupervisor ? 7 : 6} align="center"
                  sx={{ color: 'text.secondary', py: 3 }}>
                  Nenhuma proposta reprovada por CPF/CNPJ no período selecionado.
                </TableCell>
              </TableRow>
            ) : (
              linhasFiltradas.map((p, i) => (
                <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
                  <TableCell>{p.cliente?.nome}</TableCell>
                  <TableCell>{exibirCpfCnpj(p)}</TableCell>
                  <TableCell>{exibirData(p)}</TableCell>
                  <TableCell>{exibirTelefone(p)}</TableCell>
                  {isSupervisor && <TableCell align="right">{fmtBRL(p.valor)}</TableCell>}
                  <TableCell>{p.user?.nome}</TableCell>
                  <TableCell>{p.motivoNome}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <Divider sx={{ mt: 3, mb: 1 }} />
        <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
          Gerado em {new Date().toLocaleDateString('pt-BR')}
          {!isSupervisor && ' — 🔒 Dados pessoais protegidos por LGPD'}
        </Typography>
      </Paper>

      {openFiltro && (
        <PopoverFiltroRelatorio filtro={filtro} open={openFiltro}
          onClose={onCloseFiltro} onLimpar={limparFiltro}
          onConfirm={onConfirmFiltro} type={type} />
      )}
    </Box>
  );
}